/**
 * ACTIVITY-CORE — list of history entries (shared by the "Activity" page and
 * "Activity" and "History" sheet of entity detail).
 * Data-source-agnostic component: receives already-loaded rows.
 */
import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import {
  ArchiveRestore,
  CheckCircle2,
  CloudOff,
  History,
  type LucideIcon,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  Undo2,
} from "@fleet/icons";
import { type ActivityRow, groupActivityByBatch, isOfflineEdit } from "@agape/shared";
import { api } from "@/lib/api";
import { showToast } from "@/components/ui/toast";
import { Skeleton } from "@fleet/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { useI18n } from "@/providers/i18n-provider";
import { activitySemanticKey, activitySentence, formatRelativeTime } from "../lib/activity-text";

const ACTION_ICONS: Record<string, LucideIcon> = {
  created: Plus,
  completed: CheckCircle2,
  reopened: Undo2,
  updated: Pencil,
  trashed: Trash2,
  purged: Trash2,
  restored: ArchiveRestore,
  reverted: RotateCcw,
};

const ACTION_TONES: Record<string, string> = {
  created: "bg-success/10 text-success",
  completed: "bg-success/10 text-success",
  reopened: "bg-primary/10 text-primary",
  updated: "bg-primary/10 text-primary",
  trashed: "bg-destructive/10 text-destructive",
  purged: "bg-destructive/10 text-destructive",
  restored: "bg-primary/10 text-primary",
  reverted: "bg-muted text-muted-foreground",
};

interface ActivityListProps {
  rows: () => ActivityRow[];
  isLoading?: () => boolean;
  /** Groups consecutive entries from the same batch (general feed). */
  grouped?: boolean;
  /** Active user ID (for "You"). */
  currentUserId?: () => string | undefined;
  /** Shows "Undo this change" button per entry. */
  canRevert?: boolean;
}

function ActivityEntryRow(props: {
  row: ActivityRow;
  currentUserId?: string;
  canRevert?: boolean;
}) {
  const { t, locale } = useI18n();
  const [reverting, setReverting] = createSignal(false);

  const semanticKey = () => activitySemanticKey(props.row);
  const Icon = () => ACTION_ICONS[semanticKey()] ?? Pencil;
  const tone = () => ACTION_TONES[semanticKey()] ?? "bg-muted text-muted-foreground";

  const actorLabel = () => {
    if (props.row.actor_user_id && props.row.actor_user_id === props.currentUserId) {
      return t(msg`You`);
    }
    return props.row.actor_name ?? t(msg`Someone`);
  };

  const sentence = () => {
    const { descriptor, values } = activitySentence(props.row);
    return t(descriptor, values);
  };

  const serverTime = () =>
    props.row.server_ts ? new Date(props.row.server_ts).toLocaleString(locale()) : "";
  const clientTime = () =>
    props.row.client_ts ? new Date(props.row.client_ts).toLocaleString(locale()) : "";

  const handleRevert = async () => {
    if (reverting()) {
      return;
    }
    setReverting(true);
    try {
      const { error } = await api.POST("/api/activity/{id}/revert", {
        params: { path: { id: props.row.id } },
      });
      if (error) {
        showToast(error.message || t(msg`Could not undo this change`), "error");
      } else {
        showToast(t(msg`Change undone`), "success");
      }
    } catch {
      showToast(t(msg`Could not undo this change`), "error");
    } finally {
      setReverting(false);
    }
  };

  return (
    <div class="group flex items-start gap-3 py-2.5" data-testid="activity-entry">
      <div
        class={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${tone()}`}
        aria-hidden="true"
      >
        {(() => {
          const IconComp = Icon();
          return <IconComp class="h-4 w-4" />;
        })()}
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-sm leading-snug">
          <span class="font-semibold" data-testid="activity-actor">
            {actorLabel()}
          </span>{" "}
          <span data-testid="activity-sentence">{sentence()}</span>
        </p>
        <p class="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          {/* server_ts es el orden canónico; el título muestra el instante exacto. */}
          <time title={serverTime()} data-testid="activity-server-ts">
            {formatRelativeTime(props.row.server_ts, locale())}
          </time>
          <Show when={isOfflineEdit(props.row)}>
            {/* Dato secundario honesto: se hizo offline en client_ts y llegó después. */}
            <span
              class="inline-flex items-center gap-1 rounded-full bg-warning/10 px-1.5 py-0.5 text-warning"
              title={t(msg`Done offline on {date}, synced later`, { date: clientTime() })}
              data-testid="activity-offline-badge"
            >
              <CloudOff class="h-3 w-3" />
              {t(msg`Offline`)}
            </span>
          </Show>
          <Show when={props.row.platform}>
            <span class="uppercase tracking-wide opacity-70">{props.row.platform}</span>
          </Show>
        </p>
      </div>
      <Show when={props.canRevert}>
        <button
          type="button"
          class="shrink-0 rounded-md border border-border/40 px-2 py-1 text-xs font-medium text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-40"
          onClick={() => void handleRevert()}
          disabled={reverting()}
          data-testid="activity-revert"
          title={t(msg`Undo this change`)}
        >
          <span class="inline-flex items-center gap-1">
            <RotateCcw class="h-3 w-3" />
            {t(msg`Undo this change`)}
          </span>
        </button>
      </Show>
    </div>
  );
}

function ActivityListSkeleton() {
  return (
    <div class="space-y-3" data-testid="activity-skeleton">
      <For each={[0, 1, 2, 3]}>
        {() => (
          <div class="flex items-center gap-3 py-2">
            <Skeleton class="h-8 w-8 animate-shimmer rounded-full" />
            <div class="flex-1 space-y-1.5">
              <Skeleton class="h-3.5 w-3/4 animate-shimmer rounded" />
              <Skeleton class="h-3 w-1/3 animate-shimmer rounded" />
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

export function ActivityList(props: ActivityListProps) {
  const { t } = useI18n();

  const groups = () => groupActivityByBatch(props.rows());

  return (
    <Show when={!props.isLoading?.()} fallback={<ActivityListSkeleton />}>
      <Show
        when={props.rows().length > 0}
        fallback={
          <EmptyState
            icon={History}
            title={t(msg`No activity yet`)}
            description={t(msg`Changes to your tasks and lists will show up here.`)}
          />
        }
      >
        <Show
          when={props.grouped}
          fallback={
            <div class="divide-y divide-border/20" data-testid="activity-list">
              <For each={props.rows()}>
                {(row) => (
                  <ActivityEntryRow
                    row={row}
                    currentUserId={props.currentUserId?.()}
                    canRevert={props.canRevert}
                  />
                )}
              </For>
            </div>
          }
        >
          <div class="space-y-3" data-testid="activity-list">
            <For each={groups()}>
              {(group) => (
                <div class="rounded-xl border border-border/30 bg-card/60 px-4 py-1.5">
                  <Show when={group.entries.length > 1}>
                    <p class="pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t(msg`{count} changes together`, { count: String(group.entries.length) })}
                    </p>
                  </Show>
                  <div class="divide-y divide-border/15">
                    <For each={group.entries}>
                      {(row) => (
                        <ActivityEntryRow
                          row={row}
                          currentUserId={props.currentUserId?.()}
                          canRevert={props.canRevert}
                        />
                      )}
                    </For>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </Show>
  );
}
